import ApiFactory from 'services/api-factory'
import {
  AI_IMAGE_API_PATH,
  AI_IMAGE_GENERATOR_API_PATH,
  AI_IMAGE_PROJECT_API_PATH,
  API_RENDER_PATH,
  CURRENT_USER_API_PATH,
  VIDEO_PROJECTS_API_PATH,
  AI_VIDEO_API_PATH,
  FILE_UPLOAD_URL,
  CREDIT_MANAGER_API,
  RECOMMENDATION_API_PATH,
  MEDIAS_API_PATH,
  FONTS_API_PATH,
  NOTIFICATIONS_API_PATH
} from 'consts/api'
import { IProject } from 'types/project'
import { IProjectData } from 'types/projectData'
import { ITariffPlan } from 'types/tariffPlan'
import { ICampaign } from 'store/types/campaign'
import { IUser, TUserSubscriptions } from 'types/user'
import { ISoundsGenre } from 'store/types/audio'
import { IUserAudio } from 'store/types/audio'
import {
  IAIVideoGenerationResponse,
  IAIVideoGenerationRequestBody,
  IAIVideoListParams,
  IAIVideoModel,
  IAIVideoProject,
  ISceneGenerationResponse
} from 'types/aiVideo'
import {
  IGenerateImagePayload,
  IGenerateImageResponse,
  IAIImageModel,
  IAIImageStyle,
  IGetHistoryParams,
  IGetImagesByIdsParams,
  IAIImageProject
} from 'store/types/aiImage'

import { getQueryParams, getQueryStringForArrays } from 'utils'
import { IGetHistoryResponse } from 'store/types/aiImage'
import {
  IUserMediaUploadResponse,
  TUserMediaUploadSocketMessage
} from 'types/userMediaUpload'
import { IUserCreditsResponse } from 'store/types/credit'
import { IProjectMetaDataResponse } from 'types/projectMeta'
import { IUserStats } from 'types/userStats'
import {
  IAvatarGenerationRequestBody,
  IAvatarGenerationResponse,
  IAvatarGiftAccess,
  IAvatarProject,
  IPublicAvatar,
  IResizeImageRequestBody,
  IResizeImageResponse
} from 'store/types/aiAvatar'

import { waitDataFromSocket } from '../services/socket-factory'
import { CURRENT_LANGUAGE } from 'consts'
import { IAiLibVideoListParams } from 'store/types/aiLib'
import { TLibraryFonts } from 'store/types/editor'
import { IAiLibImageListParams } from 'store/types/aiLibImage'
import { IAudioItem, TUserAudioDataResponse } from 'types/audio'
import { IAvatarModels } from 'types/avatar'

const RenderApi = new ApiFactory({ baseUrl: API_RENDER_PATH })

const RecommendationApi = new ApiFactory({ baseUrl: RECOMMENDATION_API_PATH })

const VideoApi = new ApiFactory({ baseUrl: VIDEO_PROJECTS_API_PATH })
const FontsApi = new ApiFactory({ baseUrl: FONTS_API_PATH })

const AIVideoApi = new ApiFactory({ baseUrl: AI_VIDEO_API_PATH })
const AiImageApi = new ApiFactory({ baseUrl: AI_IMAGE_API_PATH })

const FileUploadApi = new ApiFactory({ baseUrl: FILE_UPLOAD_URL })

const AiImageGeneratorApi = new ApiFactory({
  baseUrl: AI_IMAGE_GENERATOR_API_PATH
})

const AiImageProjectApi = new ApiFactory({
  baseUrl: AI_IMAGE_PROJECT_API_PATH
})

const CreditManagerApi = new ApiFactory({ baseUrl: CREDIT_MANAGER_API })

const UserStatsApi = new ApiFactory({ baseUrl: MEDIAS_API_PATH })

const UserSoundsApi = new ApiFactory({ baseUrl: `${MEDIAS_API_PATH}/sounds` })

const AiLibVideoApi = new ApiFactory({ baseUrl: AI_VIDEO_API_PATH })
const AiLibImageApi = new ApiFactory({ baseUrl: AI_IMAGE_API_PATH })

const NotificationsApi = new ApiFactory({ baseUrl: NOTIFICATIONS_API_PATH })

export const getNotifications_API = () =>
  NotificationsApi.get<{ notifications: ICampaign[] }>('/notifications/')

export const markNotificationsRead_API = (ids: number[]) =>
  NotificationsApi.post<unknown, { ids: number[] }>('/notifications/read', {
    ids
  })

export const getUser_API = () => RenderApi.get<IUser>(CURRENT_USER_API_PATH)

export const getUserStats_API = () => UserStatsApi.get<IUserStats>('/stats')

export const createProject_API = (body: {
  name: string
  meta?: { referrer: string }
}) =>
  VideoApi.post<
    { project: IProject; projectData: IProjectData },
    { name: string; meta?: { referrer: string } }
  >('', body)

export const getProject_API = (id: string) => VideoApi.get<IProject>(`/${id}`)

export const getProjectMetaData_API = (id: string) =>
  RecommendationApi.get<IProjectMetaDataResponse>(`/gen-ai/project/${id}`)

export const getPublicAvatars_API = () =>
  RecommendationApi.get<IPublicAvatar[]>('/gen-ai/avatars')

export const getGiftChecker_API = () =>
  RecommendationApi.get<IAvatarGiftAccess>('/gen-ai/avatar/giftChecker')

export const patchProject_API = (id: string, data: Partial<IProject>) =>
  VideoApi.patch<IProject, Partial<IProject>>(`/${id}`, data)

export const getProjectData_API = (id: string) =>
  VideoApi.get<IProjectData>(`-data/${id}`)

export const patchProjectData_API = (id: string, data: IProjectData) =>
  VideoApi.patch<IProjectData, IProjectData>(`-data/${id}`, data)

export const renderProject_API = (projectId: string, quality: number) =>
  VideoApi.post<void, { quality: number }>(`/${projectId}/render`, {
    quality
  })

export const uploadProjectThumbnail_API = async (
  id: string,
  formData: FormData
) => VideoApi.post<IProject, FormData>(`/${id}/thumbnail`, formData)

export const deleteProjectThumbnail_API = (id: string) =>
  VideoApi.delete<IProject, void>(`/${id}/thumbnail`)
export const generateAIVideo_API = (data: IAIVideoGenerationRequestBody) =>
  AIVideoApi.post<IAIVideoProject, IAIVideoGenerationRequestBody>(
    '/videos',
    data
  )
export const generateAIVideo_RecommendationApi = (
  data: IAIVideoGenerationRequestBody
) =>
  RecommendationApi.post<
    IAIVideoGenerationResponse,
    IAIVideoGenerationRequestBody
  >('/gen-ai/generate-video', data)

export const generateConsistentAIVideo_API = (
  data: IAIVideoGenerationRequestBody
) =>
  RecommendationApi.post<
    ISceneGenerationResponse,
    IAIVideoGenerationRequestBody
  >('/gen-ai/generate-scene', data)

export const generateSmartEditedAIVideo_API = (
  data: IAIVideoGenerationRequestBody
) =>
  RecommendationApi.post<
    ISceneGenerationResponse,
    IAIVideoGenerationRequestBody
  >('/gen-ai/edit-scene', data)

export const generateAvatar_API = (data: IAvatarGenerationRequestBody) =>
  RecommendationApi.post<
    IAvatarGenerationResponse,
    IAvatarGenerationRequestBody
  >('/gen-ai/generate-avatar', data)

export const resizeImage_API = (data: IResizeImageRequestBody) =>
  RecommendationApi.post<IResizeImageResponse, IResizeImageRequestBody>(
    '/gen-ai/resize-image',
    data
  )

export const getAvatarProject_API = (avatarProjectId: number | string) =>
  RecommendationApi.get<IAvatarProject>(`/gen-ai/avatar/${avatarProjectId}`)

export const getProcessingAIVideos_API = (params: {
  model: string
  limit: number
}) =>
  AIVideoApi.get<IAIVideoProject[]>(
    `/videos?model=${encodeURIComponent(params.model)}&status=processing&limit=${params.limit}`
  )

export const getAIVideoProjects_API = (params: IAIVideoListParams) => {
  const queryParamsProjectIds = getQueryStringForArrays(
    'projectIds',
    params.projectIds?.map(String) || []
  )

  const queryParams = getQueryParams({
    limit: params.limit,
    offset: params.offset,
    videoProjectId: params.videoProjectId
  })

  return AIVideoApi.get<IAIVideoProject[]>(
    `/videos?${queryParamsProjectIds}${queryParams}`
  )
}
export const getSoundsGenres_API = (query: string) =>
  RenderApi.get<ISoundsGenre[]>(`/sounds/genres?${query}`)

export const getLibraryFonts_API = () => FontsApi.get<TLibraryFonts>(``)

export const searchSounds_API = (query: string) =>
  RenderApi.get<IUserAudio[]>(`/sounds?${query}`)

export const getUserSounds_API = () =>
  RenderApi.get<IUserAudio[]>(`/sounds/usersounds`)

export const deleteUsersSound_API = (soundId: number) =>
  RenderApi.delete<
    {
      projectIds: number[]
      videoProjectIds: number[]
      fileName: string
    },
    void
  >(`/sounds/${soundId}`)

export const getUsedLibSounds_API = (query: string) =>
  RenderApi.get<IUserAudio[][]>(`/sounds/library/by-title?${query}`)

export const getAiImgModels_API = () =>
  AiImageApi.get<IAIImageModel[]>(
    `/image-models?origin=v-editor${CURRENT_LANGUAGE !== 'en' ? `&language=${CURRENT_LANGUAGE}` : ''}`
  )

export const getAiImgStyles_API = (origin?: string) => {
  const path = origin ? `/image-styles?origin=${origin}` : '/image-styles'
  return AiImageApi.get<IAIImageStyle[]>(path)
}

export const generateAiImg_API = (
  payload: IGenerateImagePayload,
  imagesCount: number = 1
) =>
  AiImageGeneratorApi.post<IGenerateImageResponse, IGenerateImagePayload>(
    `/video-image/?imagesCount=${imagesCount}`,
    payload
  )

export const getAiImgHistory_API = ({
  limit,
  offset,
  videoProjectId
}: IGetHistoryParams) => {
  const queryParams = getQueryParams({
    limit,
    offset,
    videoProjectId
  })

  return AiImageGeneratorApi.get<IGetHistoryResponse>(
    `/user-images?${queryParams}&source=v-editor`
  )
}

export const getAiImagesByIds_API = ({ ids }: IGetImagesByIdsParams) => {
  const queryString = getQueryStringForArrays('ids', ids.map(String))

  return AiImageGeneratorApi.get<IAIImageProject[]>(
    `/user-images-by-ids?${queryString}`
  )
}

export const deleteAiImageProject_API = (imageId: number) =>
  AiImageGeneratorApi.delete(`${imageId}`)

export const regenerateAiImageProject_API = (
  imageProjectId: number | string,
  parentProjectId: number
) => AiImageGeneratorApi.post('', { imageProjectId, parentProjectId })

export const deleteAiImageProjectByProjectId_API = (projectId: number) =>
  AiImageProjectApi.delete<{ status: string; message: string }, void>(
    `/${projectId}`
  )

export const uploadFile_API = async (formData: FormData) => {
  const { data } = await FileUploadApi.post<
    {
      eventId: string
    },
    FormData
  >('/async', formData)

  const response = await waitDataFromSocket<TUserMediaUploadSocketMessage>(
    'user-media-upload',
    res => res.eventId === data.eventId
  )

  if (response.status === 'failure') {
    throw new Error(response.error.message)
  }

  return response.data
}

// Synchronous variant of the upload above: POSTs to the user-media root and
// gets the uploaded media back in the response body (no socket round-trip).
// Used for voice-clone files, which the backend stores in My Files → Sounds.
export const uploadVoiceCloneFile_API = (formData: FormData) =>
  FileUploadApi.post<IUserMediaUploadResponse, FormData>('/', formData)

export const getUserCredits_API = () =>
  CreditManagerApi.get<IUserCreditsResponse>('/user-credits/features/ai')

export const getUserTtsCredits_API = () =>
  CreditManagerApi.get<IUserCreditsResponse>('/user-credits/features/tts')

export const getUserSubscriptions_API = (params: {
  status: string
  orderBy: string
  order: string
  limit: number
}) => {
  const queryParams = getQueryParams(params)
  return RenderApi.get<TUserSubscriptions>(`/subscriptions?${queryParams}`)
}

export const getTariffPlan_API = (planId: number) =>
  RenderApi.get<ITariffPlan>(`/tariff-plans/${planId}`)

export const getAiVideoModels_API = () =>
  AIVideoApi.get<IAIVideoModel[]>(
    `/models?origin=v-editor${CURRENT_LANGUAGE !== 'en' ? `&language=${CURRENT_LANGUAGE}` : ''}`
  )

// The full, un-origin'd models catalog — includes every model (avatar included),
// unlike the `origin=v-editor` list above which is scoped to the Generate tab.
// Used by the AI History (Ai-Lib) filter so avatar clips can be filtered/labelled.
export const getAllModels_API = () =>
  AIVideoApi.get<IAIVideoModel[]>(
    `/models${CURRENT_LANGUAGE !== 'en' ? `?language=${CURRENT_LANGUAGE}` : ''}`
  )

export const getAvatarModels_API = () =>
  AIVideoApi.get<IAvatarModels[]>(`/models?origin=avatar-editor`)

export const getAiVideoVersions_API = (videoId: number | string) =>
  AIVideoApi.get<IAIVideoProject[]>(`/videos/${videoId}/versions`)

export const getAiImageVersions_API = (projectId: number | string) =>
  AiImageGeneratorApi.get<IAIImageProject[]>(
    `/user-image-versions/${projectId}`
  )

export const getAiLibVideos_API = (params: IAiLibVideoListParams) => {
  const queryParams = getQueryParams({
    limit: params.limit,
    offset: params.offset,
    videoProjectId: params.videoProjectId,
    model: params.videoModel,
    duration: params.videoDurationSeconds,
    aspectRatio: params.aspectRatio,
    quality: params.quality
  })

  return AiLibVideoApi.get<IAIVideoProject[]>(`/videos?${queryParams}`)
}

export const getAiLibImages_API = (params: IAiLibImageListParams) => {
  const queryParams = getQueryParams({
    limit: params.limit,
    offset: params.offset,
    videoProjectId: params.videoProjectId,
    model: params.model,
    aspectRatio: params.aspectRatio,
    styleId: params.styleId,
    imageType: params.imageType
  })

  return AiLibImageApi.get<IGetHistoryResponse>(
    `/image-generator/user-images?${queryParams}&source=v-editor`
  )
}

export const getUserSoundById_API = (soundId: number) =>
  UserSoundsApi.get<IAudioItem>(`/${soundId}`)

export const getUserSoundsByIds_API = (soundIds: { ids: number[] }) => {
  return UserSoundsApi.post<TUserAudioDataResponse, { ids: number[] }>(
    `/ids`,
    soundIds
  )
}
